
































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch,
} from '@vue/composition-api';
import type { Ref } from '@vue/composition-api';
import { useRouter, useFormat } from '@ideavivat/vivat-2';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import useDelivery from '@/useDelivery';

import deliveryNote from '@/table/deliveryNote/deliveryNote';
import useDeliveryNoteStatus from '@/useDeliveryNote';
import useDownloadReport from '@/useDownloadReport';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import type { ID } from '@/types';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonChip from '@/components/CommonChip.vue';
import CommonDeliveryNoteStatusSelect from '@/components/DeliveryNote/CommonDeliveryNoteStatusSelect.vue';
import CommonFormWithCashFlowEditor from '@/components/CashFlow/CommonFormWithCashFlowEditor.vue';

import getDeliveryNotes from '@/gql/deliveryNote/getDeliveryNotes.gql';
import deleteDeliveryNote from '@/gql/deliveryNote/deleteDeliveryNote.gql';
import countDeliveryNoteStatus from '@/gql/deliveryNote/countDeliveryNoteStatus.gql';
import createFuelConsumption from '@/gql/deliveryNote/createFuelConsumption.gql';
import updateDeliveryNote from '@/gql/deliveryNote/updateDeliveryNote.gql';

const NOW = useFormat.date().computerShortDate();

const VehicleList = defineComponent({
  components: {
    CommonModalWithPreset,
    CommonChip,
    CommonDeliveryNoteStatusSelect,
    CommonFormWithCashFlowEditor,
  },
  setup() {
    const { translate: translateDeliveryNoteStatus } = useDeliveryNoteStatus();
    const { getPDFReport } = useDownloadReport();
    const { farthestDeliveryAreaCode } = useDeliveryAreaCode();
    const { router, route } = useRouter();
    const { type, typeDisplay } = useDelivery();
    const toCurrency = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const onRowClick = (row: any) => {
      router.push({ name: 'DeliveryNoteInformation', params: { id: row.item.id } });
    };

    const totalWeightComputed = (val: any) => val.reduce((a: any, b: any) => a + b.totalWeight, 0).toFixed(3);
    const totalAmountComputed = (val: any) => val.reduce((a: any, b: any) => a + b.packages.length, 0);

    const filter = reactive({
      status: '',
      text: '',
      orderDate: {
        from: undefined,
        to: undefined,
      },
      typeFilter: undefined,
    });
    function clearFilter() {
      filter.status = '';
      filter.text = '';
      filter.orderDate.from = undefined;
      filter.orderDate.to = undefined;
    }
    function clickDateHandler(date: any) {
      if (filter.orderDate.from === useFormat.date(date).computerShortDate()) {
        filter.orderDate = {
          from: undefined,
          to: undefined,
        };
      } else {
        filter.orderDate = {
          from: useFormat.date(date).computerShortDate(),
          to: useFormat.date(date).computerShortDate(),
        };
      }
    }

    const computedDateInput = computed(() => ({
      from: filter.orderDate.from ? `${filter.orderDate.from} 00:00:00` : undefined,
      to: filter.orderDate.from ? `${filter.orderDate.to} 23:59:59` : undefined,
    }));

    const deliveryNoteVariables = computed(() => {
      const vars: {[key: string]: any} = {
        searchDeliveryNoteId: undefined,
        searchDriverName: undefined,
        filterByStatus: undefined,
        filterUpdatedAtBetween: undefined,
        filterByType: undefined,
      };
      if (filter.text) {
        vars.searchDeliveryNoteId = `%${filter.text}%`;
        // vars.searchDriverName = `%${filter.text}`;
      }
      if (filter.status) {
        vars.filterByStatus = filter.status;
      }
      if (filter.orderDate.from) {
        vars.filterDeliveryDateBetween = filter.orderDate;
      }
      if (filter.typeFilter) vars.filterByType = filter.typeFilter;
      return vars;
    });
    const getDeliveryNotesResult = (data: any) => data.deliveryNotes;

    // ==============================================
    // ===== Use to delete Delivery from system =====
    // ==============================================
    const refetchQueriesList = [
      'getDeliveryNotes', 'countDeliveryNote', 'getDeliveries', 'countDeliveryNoteStatus',
    ];
    const deleteDeliveryNoteModal = ref(null);
    const deleteDeliveryNoteMutation = useMutation(deleteDeliveryNote, {
      refetchQueries: refetchQueriesList,
    });
    function deleteDeliveryNoteAction(deliveryId: number) {
      deleteDeliveryNoteMutation.mutate({
        id: deliveryId,
      });
    }

    // count delivery note to display on top panel
    const dnStatusCounter = reactive({
      PREPARE_TO_DELIVER: 0,
      ON_THE_WAY: 0,
      SOME_SUCCESS: 0,
      SUCCESS: 0,
      NOT_SUCCESS: 0,
      HAS_CANCELED_ORDER: 0,
      total: 0,
    });
    const { result: getCountDeliveryNoteResult, loading: countDeliveryLoading } = useQuery(countDeliveryNoteStatus, () => ({
      filterUpdatedAtBetween: computedDateInput.value.from ? computedDateInput.value : undefined,
    }), () => ({
      fetchPolicy: 'network-only',
    }));
    const countDeliveryNoteResult = useResult(getCountDeliveryNoteResult, [
      { status: 'PREPARE_TO_DELIVER', count: 0 },
      { status: 'ON_THE_WAY', count: 0 },
      { status: 'SOME_SUCCESS', count: 0 },
      { status: 'SUCCESS', count: 0 },
      { status: 'NOT_SUCCESS', count: 0 },
      { status: 'HAS_CANCELED_ORDER', count: 0 },
    ], (data) => data.countTodayDeliveryNote);
    const totalDeliveryNoteResult = useResult(getCountDeliveryNoteResult, 0, (data) => data.totalDeliveryNote.paginatorInfo.total);
    watch(() => getCountDeliveryNoteResult.value, (newVal, oldVal) => {
      if (!countDeliveryLoading.value && newVal !== oldVal) {
        dnStatusCounter.total = totalDeliveryNoteResult.value as any;
        dnStatusCounter.PREPARE_TO_DELIVER = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'PREPARE_TO_DELIVER')?.count || 0;
        dnStatusCounter.ON_THE_WAY = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'ON_THE_WAY')?.count || 0;
        dnStatusCounter.SUCCESS = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'SUCCESS')?.count || 0;
        dnStatusCounter.SOME_SUCCESS = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'SOME_SUCCESS')?.count || 0;
        dnStatusCounter.NOT_SUCCESS = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'NOT_SUCCESS')?.count || 0;
        dnStatusCounter.HAS_CANCELED_ORDER = countDeliveryNoteResult.value.find((countObj: any) => countObj.status === 'HAS_CANCELED_ORDER')?.count || 0;
      }
    });

    const addFuelConsumptionModal = ref(null);
    const fuelConsumption = reactive({
      amount: '0',
      date: NOW,
      deliveryNoteId: '-1',
      vehicleId: '-1',
      driverId: '-1',
      deliveryNote: { id: -1 },
    });
    function pendingAddFuelConsumption(deliveryNoteFn: any, vehicleId: string, driverId: string) {
      fuelConsumption.deliveryNoteId = deliveryNoteFn.id;
      fuelConsumption.deliveryNote = deliveryNoteFn;
      fuelConsumption.vehicleId = vehicleId;
      fuelConsumption.driverId = driverId;
      (addFuelConsumptionModal.value as any).open();
    }
    const createFuelConsumptionMutation = useMutation(createFuelConsumption, () => ({
      variables: {
        date: fuelConsumption.date,
        amount: parseInt(fuelConsumption.amount, 10),
        deliveryNoteId: parseInt(fuelConsumption.deliveryNoteId, 10),
        vehicleId: parseInt(fuelConsumption.vehicleId, 10),
        driverId: parseInt(fuelConsumption.driverId, 10),
      },
      refetchQueries: ['getDeliveryNotes'],
    }));
    createFuelConsumptionMutation.onDone(() => {
      (addFuelConsumptionModal.value as any).close();
    });

    const editTravelDistanceModal = ref(null);
    const travelDistance = reactive({
      deliveryNoteId: '-1',
      vehicleId: '-1',
      driverId: '-1',
      deliveryNote: { id: -1 },
      distance: '0',
      fuelConsumed: 0,
      startMileNumber: '0',
      endMileNumber: '0',
      fuelCost: 0,
      fuelType: 'DIESEL',
    });
    function parseMile(val: string | number) { return parseFloat((val as number).toFixed(2)); }
    const computedTravelDistance = computed(() => parseMile(parseFloat(travelDistance.endMileNumber) - parseFloat(travelDistance.startMileNumber) || 0));
    function pendingEditTravelDistance(deliveryNoteFn: any, vehicleId: string, driverId: string) {
      travelDistance.deliveryNoteId = deliveryNoteFn.id;
      travelDistance.deliveryNote = deliveryNoteFn;
      travelDistance.vehicleId = vehicleId;
      travelDistance.driverId = driverId;
      travelDistance.distance = deliveryNoteFn.distanceTravelled;
      travelDistance.fuelConsumed = deliveryNoteFn.fuelConsumed;
      travelDistance.startMileNumber = deliveryNoteFn.startMileNumber;
      travelDistance.endMileNumber = deliveryNoteFn.endMileNumber;
      travelDistance.fuelCost = deliveryNoteFn.fuelCost;
      (editTravelDistanceModal.value as any).open();
    }
    const updateDeliveryNoteMutation = useMutation(updateDeliveryNote, () => ({
      variables: {
        id: travelDistance.deliveryNoteId,
        input: {
          distanceTravelled: computedTravelDistance.value || 0,
          startMileNumber: parseFloat(travelDistance.startMileNumber as string),
          endMileNumber: parseFloat(travelDistance.endMileNumber as string),
          fuelConsumed: travelDistance.fuelConsumed,
          fuelCost: travelDistance.fuelCost,
          fuelType: travelDistance.fuelType,
        },
      },
      refetchQueries: ['getDeliveryNotes'],
    }));
    updateDeliveryNoteMutation.onDone(() => {
      (editTravelDistanceModal.value as any).close();
    });
    watch(() => route.value.query, (newVal, oldVal) => {
      if (route.value.query) {
        filter.status = route.value.query.s;
      }
    });

    const chosenDeliveryNote: Ref<ID> = ref(-1);
    const createCashflowModal = ref(null);
    function openCashflowModal(id: ID) {
      chosenDeliveryNote.value = id;
      (createCashflowModal.value as any).open();
    }

    return {
      toCurrency,
      farthestDeliveryAreaCode,
      translateDeliveryNoteStatus,
      onRowClick,

      type,
      typeDisplay,
      clickDateHandler,
      clearFilter,
      filter,

      getDeliveryNotes,
      deliveryNoteVariables,
      getDeliveryNotesResult,
      deliveryNoteTableOptions: deliveryNote,

      totalWeightComputed,
      totalAmountComputed,

      deleteDeliveryNoteModal,
      deleteDeliveryNoteMutation,
      deleteDeliveryNoteAction,

      dnStatusCounter,

      getPDFReport,

      // Fuel consumption
      addFuelConsumptionModal,
      fuelConsumption,
      pendingAddFuelConsumption,
      createFuelConsumptionMutation,

      // Travel Distance
      editTravelDistanceModal,
      travelDistance,
      computedTravelDistance,
      pendingEditTravelDistance,
      updateDeliveryNoteMutation,

      chosenDeliveryNote,
      createCashflowModal,
      openCashflowModal,
    };
  },
});

export default VehicleList;
